// Land layer (lane C, backlog C-3; territory-visuals.md §1): one cached bitmap per 16 × 16-cell
// chunk, redrawn only when the store marks it changed (its version, a player colour arriving, a
// new cache resolution, a new painter). Each owner's land is its smoothed contour loops
// (contour.ts: marching squares, Gaussian along the arc, one Chaikin pass) as one nonzero path,
// filled and then stroked LAND_SEAL_CELLS wide in the same paint so the hairline ground slivers
// where two owners' loops part at a triple junction close up. The loops are traced over the chunk
// ± (CHUNK_PAD + SMOOTH_PAD) cells while the bitmap keeps only ± CHUNK_PAD, so the window edge's
// distortion is clipped away and neighbouring chunks draw identical vertices (no seams). The
// store bumps a changed chunk's 8 neighbours, which covers the smoothing reach (≤ 5 cells).
//
// Skins: every owner's paint goes through one `LandPainter` (`painter`), which gets the path
// already built in world cells (a pattern fill anchored to the world just works). The renderer
// installs arena/skins.ts `skinLandPainter` (flat fill + the owner's pattern); a chunk redraws
// when its version, the PLAYERS set (colour or skin changed) or the painter changes.
//
// The 2.5D look is composed by the renderer from these bitmaps in two passes: the SIDE pass
// (every chunk at ground level, then darkened to V × sideShade with a source-atop black at
// 1 − sideShade) and the TOP pass (every chunk lifted ARENA_CLIENT.liftCells up the screen), so
// the side band only shows along bottom-facing edges. Player colours are our own palette
// (D-V2); the bitmaps hold main colours only.
//
// Cache: bitmaps at a resolution on a 2^(1/4) ladder of the screen's device px per cell (capped,
// so memory stays bounded), least-recently-used eviction past MAX_BITMAPS. Redraw ≤ 1 ms per
// dirty chunk.

import { ARENA_NET, type Rgb } from '@ballio/shared';
import { type Canvas2D, type Ctx2D, createCanvas, get2d, sizeCanvas } from '../render/canvas.js';
import { css } from '../render/theme.js';
import type { ArenaView } from './camera.js';
import { pathPolygons, SMOOTH_PAD, smoothLoop, traceOwned } from './contour.js';
import type { ArenaStore } from './store.js';

const CHUNK = ARENA_NET.chunkSize;
/** Cells of padding around each chunk bitmap. */
export const CHUNK_PAD = 1;
/** The seal stroke around each owner's land (cells; LV-1). */
export const LAND_SEAL_CELLS = 0.12;

/** One owner's land as handed to the painter. */
export interface LandOwner {
  readonly id: number;
  /** The player's palette colour (main shade). */
  readonly color: Rgb;
  /** The player's arena skin id (`""` = none): a skin painter fills its pattern (SK-3). */
  readonly skinId: string;
}

/**
 * Paints one owner's land: `ctx`'s current path holds the owner's smoothed loops in world cells
 * (the transform maps cells to bitmap px; nonzero winding). The default fills the main colour and
 * seals it with a LAND_SEAL_CELLS stroke; a skin's painter may fill a pattern instead.
 */
export type LandPainter = (ctx: Ctx2D, owner: LandOwner) => void;

export const defaultLandPainter: LandPainter = (ctx, owner) => {
  const c = css(owner.color);
  ctx.fillStyle = c;
  ctx.fill();
  ctx.strokeStyle = c;
  ctx.lineWidth = LAND_SEAL_CELLS;
  ctx.lineJoin = 'round';
  ctx.stroke();
};

/** Bitmaps kept at most (≈ 3 screens of chunks). */
export const MAX_BITMAPS = 72;
/** Cache resolutions, device px per cell. */
export const MIN_CACHE_PX = 4;
export const MAX_CACHE_PX = 40;

/** The cache resolution for a screen resolution (device px per cell). */
export function cacheResolution(devicePxPerCell: number): number {
  if (!(devicePxPerCell > 0)) return MIN_CACHE_PX;
  const k = Math.ceil(Math.log2(devicePxPerCell) * 4 - 1e-9);
  const px = Math.pow(2, k / 4);
  return Math.min(MAX_CACHE_PX, Math.max(MIN_CACHE_PX, Math.ceil(px)));
}

/**
 * Chunk bitmaps redrawn per frame at most, when only the cache resolution changed (a zoom
 * crossing a 2^(1/4) step, e.g. the results zoom-out): the others keep drawing their old bitmap,
 * scaled, until their turn. Content changes and chunks never drawn are not limited.
 */
export const MAX_RESCALES_PER_FRAME = 4;

/**
 * Owners with at most this many cells in a chunk's window whose loop the window clips are traced
 * from their whole (8-connected) land instead, if that is at most this many cells: a spawn blob
 * (81 cells) on a chunk border then gets the same round outline in every chunk.
 */
export const WHOLE_OWNER_CELLS = 200;

interface ChunkBitmap {
  canvas: Canvas2D;
  ctx: Ctx2D;
  version: number;
  /** store.playersVersion when the drawn looks were last checked. */
  players: number;
  /** Each owner drawn: [id, colour, skin id] (a PLAYERS change redraws only if one differs). */
  looks: [number, Rgb, string][];
  painter: number;
  px: number;
  empty: boolean;
  used: number;
}

/** Some loop vertex lies on the window's edge (the window cut the land there). */
function clipped(loops: readonly number[][], wx0: number, wy0: number, ww: number): boolean {
  const x1 = wx0 + ww;
  const y1 = wy0 + ww;
  for (const l of loops) {
    for (let i = 0; i < l.length; i += 2) {
      const x = l[i] as number;
      const y = l[i + 1] as number;
      if (x <= wx0 || y <= wy0 || x >= x1 || y >= y1) return true;
    }
  }
  return false;
}

export class LandLayer {
  private readonly store: ArenaStore;
  private readonly cache = new Map<number, ChunkBitmap>();
  private epoch = -1;
  private useCounter = 0;
  private painterVersion = 0;
  private paint: LandPainter = defaultLandPainter;
  /** Chunks redrawn by the last `prepare` (stats, tests). */
  redrawn = 0;

  constructor(store: ArenaStore) {
    this.store = store;
  }

  /** The land painter (skins); setting it redraws every chunk. */
  get painter(): LandPainter {
    return this.paint;
  }

  set painter(p: LandPainter) {
    if (p === this.paint) return;
    this.paint = p;
    this.invalidate();
  }

  /** Redraw every chunk on next use (a painter's look changed). */
  invalidate(): void {
    this.painterVersion++;
  }

  /** Chunk index range in view. */
  visibleChunks(view: ArenaView): { kx0: number; ky0: number; kx1: number; ky1: number } {
    const hw = view.width / 2 / view.scale + CHUNK_PAD;
    const hh = view.height / 2 / view.scale + CHUNK_PAD + 1;
    const s = this.store;
    return {
      kx0: Math.max(0, Math.floor((view.cx - hw) / CHUNK)),
      ky0: Math.max(0, Math.floor((view.cy - hh) / CHUNK)),
      kx1: Math.min(s.chunksX - 1, Math.floor((view.cx + hw) / CHUNK)),
      ky1: Math.min(s.chunksY - 1, Math.floor((view.cy + hh) / CHUNK)),
    };
  }

  /**
   * Redraws the visible chunks that changed; returns how many were redrawn. A chunk redraws when
   * its land version or the painter changed, or an owner it drew has a new colour or skin (a
   * new player's first record only redraws the chunks that drew it in the fallback grey). A
   * chunk whose only change is the cache resolution waits its turn (MAX_RESCALES_PER_FRAME).
   */
  prepare(view: ArenaView, deviceScale: number): number {
    const s = this.store;
    if (s.landEpoch !== this.epoch) {
      this.epoch = s.landEpoch;
      for (const b of this.cache.values()) b.version = -1;
    }
    const px = cacheResolution(view.scale * deviceScale);
    const r = this.visibleChunks(view);
    this.redrawn = 0;
    let rescales = 0;
    for (let ky = r.ky0; ky <= r.ky1; ky++) {
      for (let kx = r.kx0; kx <= r.kx1; kx++) {
        const i = ky * s.chunksX + kx;
        const b = this.cache.get(i);
        if (b) {
          b.used = ++this.useCounter;
          if (b.players !== s.playersVersion && !this.looksChanged(b)) b.players = s.playersVersion;
          const same =
            b.version === (s.chunkVersion[i] as number) &&
            b.players === s.playersVersion &&
            b.painter === this.painterVersion;
          if (same && b.px === px) continue;
          if (same && rescales >= MAX_RESCALES_PER_FRAME) continue;
          if (same) rescales++;
        }
        this.drawChunk(kx, ky, px, b);
        this.redrawn++;
      }
    }
    this.evict();
    return this.redrawn;
  }

  /** An owner the bitmap drew has another colour or skin now. */
  private looksChanged(b: ChunkBitmap): boolean {
    const s = this.store;
    for (const [id, color, skin] of b.looks) {
      if (s.colorOf(id) !== color || s.skinOf(id) !== skin) return true;
    }
    return false;
  }

  /**
   * Blits the visible chunk bitmaps into `ctx` (CSS px transform already set), shifted by
   * `offsetY` CSS px. Call `prepare` first in the frame.
   */
  blit(ctx: Ctx2D, view: ArenaView, offsetY: number): void {
    const s = this.store;
    const r = this.visibleChunks(view);
    const scale = view.scale;
    const ox = view.width / 2 - view.cx * scale;
    const oy = view.height / 2 - view.cy * scale + offsetY;
    for (let ky = r.ky0; ky <= r.ky1; ky++) {
      for (let kx = r.kx0; kx <= r.kx1; kx++) {
        const b = this.cache.get(ky * s.chunksX + kx);
        if (!b || b.empty) continue;
        const span = CHUNK + CHUNK_PAD * 2;
        const dx = ox + (kx * CHUNK - CHUNK_PAD) * scale;
        const dy = oy + (ky * CHUNK - CHUNK_PAD) * scale;
        ctx.drawImage(b.canvas as CanvasImageSource, dx, dy, span * scale, span * scale);
      }
    }
  }

  /** Forget every bitmap (context loss, a new room). */
  clear(): void {
    this.cache.clear();
  }

  private drawChunk(kx: number, ky: number, px: number, reuse: ChunkBitmap | undefined): void {
    const s = this.store;
    const i = ky * s.chunksX + kx;
    const span = CHUNK + CHUNK_PAD * 2;
    const size = span * px;
    let b = reuse;
    if (!b) {
      let canvas: Canvas2D;
      try {
        canvas = createCanvas(size, size);
      } catch {
        return;
      }
      const ctx = get2d(canvas);
      if (!ctx) return;
      b = {
        canvas,
        ctx,
        version: -1,
        players: -1,
        looks: [],
        painter: -1,
        px,
        empty: true,
        used: 0,
      };
      this.cache.set(i, b);
    }
    sizeCanvas(b.canvas, size, size);
    const ctx = b.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, size, size);
    const x0 = kx * CHUNK - CHUNK_PAD;
    const y0 = ky * CHUNK - CHUNK_PAD;
    // The sample window: the bitmap ± SMOOTH_PAD, read once, with a ring of unowned samples.
    const wx0 = x0 - SMOOTH_PAD;
    const wy0 = y0 - SMOOTH_PAD;
    const ww = span + SMOOTH_PAD * 2;
    const sw = ww + 2;
    const win = new Int32Array(sw * sw);
    const ids: number[] = [];
    /** Cells per owner in the window, and whether any lies within NEAR of the bitmap. */
    const counts: number[] = [];
    const near: boolean[] = [];
    const NEAR = SMOOTH_PAD - 2; // a loop more than 2 cells outside the bitmap never reaches it
    for (let y = 0; y < ww; y++) {
      for (let x = 0; x < ww; x++) {
        const id = s.ownerAt(wx0 + x, wy0 + y);
        if (id === 0) continue;
        win[(y + 1) * sw + x + 1] = id;
        let k = ids.indexOf(id);
        if (k < 0) {
          k = ids.push(id) - 1;
          counts.push(0);
          near.push(false);
        }
        counts[k] = (counts[k] as number) + 1;
        if (x >= NEAR && y >= NEAR && x < ww - NEAR && y < ww - NEAR) near[k] = true;
      }
    }
    b.looks = [];
    b.empty = !near.includes(true);
    if (!b.empty) {
      ctx.setTransform(px, 0, 0, px, -x0 * px, -y0 * px);
      const own = new Uint8Array(sw * sw);
      for (let k = 0; k < ids.length; k++) {
        if (!near[k]) continue;
        const id = ids[k] as number;
        for (let c = 0; c < win.length; c++) own[c] = win[c] === id ? 1 : 0;
        let loops = traceOwned(own, sw, sw, wx0 - 1, wy0 - 1);
        if (loops.length === 0) continue;
        if ((counts[k] as number) <= WHOLE_OWNER_CELLS && clipped(loops, wx0, wy0, ww)) {
          loops = this.wholeLoops(id, wx0, wy0, ww) ?? loops;
        }
        ctx.beginPath();
        pathPolygons(
          ctx,
          loops.map((l) => smoothLoop(l)),
        );
        const look: [number, Rgb, string] = [id, s.colorOf(id), s.skinOf(id)];
        b.looks.push(look);
        this.paint(ctx, { id, color: look[1], skinId: look[2] });
      }
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    b.version = s.chunkVersion[i] as number;
    b.players = s.playersVersion;
    b.painter = this.painterVersion;
    b.px = px;
    b.used = ++this.useCounter;
  }

  /**
   * The raw loops of owner `id`'s whole land reachable (8-connected, as the contour joins
   * diagonals) from its cells in the window, or null when that exceeds WHOLE_OWNER_CELLS.
   */
  private wholeLoops(id: number, wx0: number, wy0: number, ww: number): number[][] | null {
    const s = this.store;
    const cols = s.cols;
    const seen = new Set<number>();
    const stack: number[] = [];
    for (let y = wy0; y < wy0 + ww; y++) {
      for (let x = wx0; x < wx0 + ww; x++) {
        if (s.ownerAt(x, y) !== id) continue;
        const c = y * cols + x;
        if (!seen.has(c)) {
          seen.add(c);
          stack.push(c);
        }
      }
    }
    while (stack.length > 0) {
      const c = stack.pop() as number;
      const cx = c % cols;
      const cy = (c - cx) / cols;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (s.ownerAt(cx + dx, cy + dy) !== id) continue;
          const n = (cy + dy) * cols + cx + dx;
          if (seen.has(n)) continue;
          if (seen.size >= WHOLE_OWNER_CELLS) return null;
          seen.add(n);
          stack.push(n);
        }
      }
    }
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const c of seen) {
      const x = c % cols;
      const y = (c - x) / cols;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    const w = maxX - minX + 3;
    const h = maxY - minY + 3;
    const own = new Uint8Array(w * h);
    for (const c of seen) {
      const x = c % cols;
      const y = (c - x) / cols;
      own[(y - minY + 1) * w + (x - minX + 1)] = 1;
    }
    return traceOwned(own, w, h, minX - 1, minY - 1);
  }

  private evict(): void {
    if (this.cache.size <= MAX_BITMAPS) return;
    const byAge = [...this.cache.entries()].sort((a, b) => a[1].used - b[1].used);
    for (let k = 0; k < byAge.length - MAX_BITMAPS; k++) {
      this.cache.delete((byAge[k] as [number, ChunkBitmap])[0]);
    }
  }
}
